import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Crosshair, Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const u = await login(email, password);
            toast.success("Bienvenido a FindMed Dispatch Control");
            // Routing por rol: cliente → portal, ambulancia/paramedico/operador → crew móvil, resto → dashboard
            if (u?.role === "cliente") {
                navigate("/portal");
            } else if (["ambulancia", "paramedico", "operador", "medico", "enfermera"].includes(u?.role)) {
                navigate("/crew");
            } else {
                navigate("/dashboard");
            }
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-slate-100">
            {/* Left: Brand panel */}
            <div className="hidden lg:flex flex-col w-1/2 bg-slate-900 text-white p-12 relative overflow-hidden fm-grain">
                <div className="relative z-10 flex flex-col h-full">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-red-600 flex items-center justify-center rounded-sm">
                            <Crosshair className="w-6 h-6 text-white" strokeWidth={2} />
                        </div>
                        <div>
                            <div className="font-display font-black text-xl tracking-tight">FindMed</div>
                            <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">Dispatch Control</div>
                        </div>
                    </div>

                    <div className="my-auto py-10">
                        <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold mb-3">
                            EMS · Operación Médica Privada · México
                        </div>
                        <h1 className="font-display font-black text-5xl tracking-tighter leading-[1.05] mb-6">
                            Central de despacho<br />
                            para <span className="text-red-500">ambulancias</span> de<br />alto nivel.
                        </h1>
                        <p className="text-slate-300 text-sm leading-relaxed max-w-md">
                            Cotización, despacho, geolocalización, FRAP digital, finanzas operativas y trazabilidad
                            completa en una sola plataforma. Diseñada para clientes corporativos, hospitales,
                            aseguradoras y operación 24/7.
                        </p>

                        <div className="grid grid-cols-3 gap-4 mt-10 max-w-md">
                            {[
                                { v: "25", l: "Módulos" },
                                { v: "27", l: "Estados de servicio" },
                                { v: "24/7", l: "Operación" },
                            ].map((s) => (
                                <div key={s.l} className="border-l-2 border-red-600 pl-3">
                                    <div className="font-mono text-2xl font-bold tabular-nums">{s.v}</div>
                                    <div className="text-[10px] text-slate-400 uppercase tracking-widest">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="text-[10px] text-slate-500 uppercase tracking-widest">
                        © 2026 FindMed · Sistema certificado · Cumplimiento NOM-237-SSA1
                    </div>
                </div>
            </div>

            {/* Right: Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <div className="lg:hidden flex items-center gap-3 mb-8">
                        <div className="w-10 h-10 bg-slate-900 flex items-center justify-center rounded-sm">
                            <Crosshair className="w-5 h-5 text-white" strokeWidth={2} />
                        </div>
                        <div>
                            <div className="font-display font-black text-lg tracking-tight">FindMed</div>
                            <div className="text-[10px] text-slate-500 uppercase tracking-widest">Dispatch Control</div>
                        </div>
                    </div>

                    <div className="mb-8">
                        <div className="inline-flex items-center gap-2 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-sm mb-4">
                            <Activity className="w-3 h-3 text-emerald-600" strokeWidth={2} />
                            <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest">Sistema en línea</span>
                        </div>
                        <h2 className="font-display font-black text-3xl tracking-tight text-slate-900 mb-1">
                            Iniciar sesión
                        </h2>
                        <p className="text-sm text-slate-500">
                            Acceso restringido a personal autorizado de FindMed.
                        </p>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                        <div>
                            <label className="fm-label">Correo electrónico</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="usuario@findmed.com"
                                className="fm-input"
                                required
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <label className="fm-label">Contraseña</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="fm-input"
                                required
                                data-testid="login-password-input"
                            />
                        </div>

                        {error && (
                            <div className="fm-badge fm-badge-critical w-full justify-center py-2" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full fm-btn-primary justify-center py-2.5"
                            data-testid="login-submit-btn"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            {loading ? "Verificando..." : "Acceder al sistema"}
                        </button>

                        <div className="text-center">
                            <Link
                                to="/forgot-password"
                                className="text-xs text-slate-600 hover:text-red-600 hover:underline font-medium"
                                data-testid="forgot-password-link"
                            >
                                ¿Olvidaste tu contraseña?
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
